import type { ReactNode } from "react"
import { useState } from "react"
import { IconDotsOutline18, IconFilterOutline18 } from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"
import { IconButton, Tabs, TabsList, TabsTrigger } from "@productlane/ui"

import EmptyInbox from "@/assets/empty-states/inbox/empty-inbox.png"
import { isDevEnvironment } from "@/env"
import { NOTIFICATIONS_COPY as copy } from "@/screens/app/notifications/copy"
import { NotificationListEmpty } from "@/screens/app/notifications/notification-list-empty"
import { NotFound } from "@/screens/not-found"

type View = "inbox" | "unread" | "mentions"
const VIEWS: View[] = ["inbox", "unread", "mentions"]

function ListHeader({
	view,
	onView,
}: {
	view: View
	onView: (v: View) => void
}) {
	return (
		<>
			<div className="flex h-12 flex-none items-center justify-between gap-2 px-3">
				<div className="flex items-center gap-1">
					<h1 className="text-sm font-medium tracking-tight text-primary">
						{copy.panelTitle}
					</h1>
					<IconButton
						variant="ghost"
						aria-label={copy.actions.more}
						Icon={IconDotsOutline18}
					/>
				</div>
				<IconButton
					variant="ghost"
					aria-label="Filter"
					Icon={IconFilterOutline18}
				/>
			</div>
			<div className="flex flex-none items-center px-3 pb-2">
				<Tabs value={view} onValueChange={(v) => onView(v as View)}>
					<TabsList>
						{VIEWS.map((v) => (
							<TabsTrigger key={v} value={v}>
								{copy.views[v]}
							</TabsTrigger>
						))}
					</TabsList>
				</Tabs>
			</div>
		</>
	)
}

function ListEmpty({ view }: { view: View }) {
	return <NotificationListEmpty title={copy.empty[view]} />
}

function Frame({ children }: { children: ReactNode }) {
	return (
		<div className="flex h-[560px] overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700/80 dark:bg-gray-950">
			{children}
		</div>
	)
}

function ListColumn({
	view,
	onView,
	children,
	full,
}: {
	view: View
	onView: (v: View) => void
	children?: ReactNode
	full?: boolean
}) {
	return (
		<section
			className={cn(
				"flex h-full min-w-0 flex-col",
				full
					? "flex-1"
					: "w-[360px] flex-none border-r border-gray-200 dark:border-gray-700/80",
			)}
		>
			<ListHeader view={view} onView={onView} />
			<div className="flex min-h-0 flex-1 flex-col">{children}</div>
		</section>
	)
}

function Today({ view, onView }: { view: View; onView: (v: View) => void }) {
	return (
		<Frame>
			<ListColumn view={view} onView={onView}>
				<ListEmpty view={view} />
			</ListColumn>
			<div className="flex flex-1 flex-col items-center justify-center gap-4 p-8 pb-10">
				<img
					src={EmptyInbox}
					alt=""
					className="h-auto w-[4.05rem] opacity-80 dark:opacity-50"
				/>
				<p className="text-sm text-tertiary">{copy.detail.noUnread}</p>
			</div>
		</Frame>
	)
}

function OptionDetailPane({
	view,
	onView,
}: {
	view: View
	onView: (v: View) => void
}) {
	return (
		<Frame>
			<ListColumn view={view} onView={onView} />
			<div className="flex flex-1">
				<ListEmpty view={view} />
			</div>
		</Frame>
	)
}

function OptionFullWidth({
	view,
	onView,
}: {
	view: View
	onView: (v: View) => void
}) {
	return (
		<Frame>
			<ListColumn view={view} onView={onView} full>
				<ListEmpty view={view} />
			</ListColumn>
		</Frame>
	)
}

function OptionBlankDetail({
	view,
	onView,
}: {
	view: View
	onView: (v: View) => void
}) {
	return (
		<Frame>
			<ListColumn view={view} onView={onView}>
				<ListEmpty view={view} />
			</ListColumn>
			<div className="flex-1" />
		</Frame>
	)
}

const OPTIONS = [
	{ name: "Today", note: "Two empty states side by side", Render: Today },
	{
		name: "1. One empty state in the detail pane (recommended)",
		note: "List keeps its tabs and stays blank; the message sits in the big pane",
		Render: OptionDetailPane,
	},
	{
		name: "2. List takes the full width",
		note: "Detail pane hides until there is something to open",
		Render: OptionFullWidth,
	},
	{
		name: "3. Blank detail pane",
		note: "Smallest change: drop the detail pane's message",
		Render: OptionBlankDetail,
	},
]

export function NotificationsEmptyPage() {
	const [view, setView] = useState<View>("inbox")
	if (!isDevEnvironment) return <NotFound />

	return (
		<div className="h-full overflow-y-auto bg-gray-50 dark:bg-gray-900">
			<div className="mx-auto max-w-[1200px] px-6 py-10 pb-24">
				<div className="mb-8">
					<div className="text-lg font-semibold text-primary">
						Notifications empty state, three options
					</div>
					<div className="text-sm text-secondary">
						Switch the tabs in any frame; all frames follow.
					</div>
				</div>
				<div className="flex flex-col gap-10">
					{OPTIONS.map(({ name, note, Render }) => (
						<div key={name} className="flex flex-col gap-2">
							<div>
								<div className="text-sm font-medium text-primary">{name}</div>
								<div className="text-xs text-secondary">{note}</div>
							</div>
							<Render view={view} onView={setView} />
						</div>
					))}
				</div>
			</div>
		</div>
	)
}
